/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type VehicleSummary } from '@/types';

export const formatBaseVehicle = (vehicleMap?: VehicleSummary): string => {
  const year = vehicleMap?.year ?? '';
  const makeId = vehicleMap?.makeId ?? '';
  const make = vehicleMap?.make ?? '';
  const modelId = vehicleMap?.modelId ?? '';
  const model = vehicleMap?.model ?? '';
  const vehicleTypeId = vehicleMap?.vehicleTypeId ?? '';
  const vehicleTypeName = vehicleMap?.vehicleTypeName ?? '';

  return `${year}|${makeId}|${make}|${modelId}|${model}|${vehicleTypeId}|${vehicleTypeName}`;
};

export const formatVehicleQuestions = (vehicleMap?: VehicleSummary): string => {
  const vehicleQuestionsIds = vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds ?? {};
  const vehicleQuestionsNames = vehicleMap?.acesVehicleAttributes?.vehicleQuestionsNames ?? {};

  return `${Object.values(vehicleQuestionsIds).join('|')}|${Object.values(
    vehicleQuestionsNames,
  ).join('|')}`;
};

export const formatVehicleInformation = (vehicleMap?: VehicleSummary) => {
  const vehicleTypeName = vehicleMap?.vehicleTypeName ?? '';
  const year = vehicleMap?.year ?? '';
  const make = vehicleMap?.make ?? '';
  const model = vehicleMap?.model ?? '';
  return `${vehicleTypeName}|${year}|${make}|${model}`;
};

/**
 * Formats vehicle data in ACES YMME format for Monetate
 * Returns an array of strings with progressive replacement of engineBaseId segments:
 * 1. Full format: Year_MakeID_ModelID_SubmodelID_EngineBaseID (e.g., 2006_73_986_123_2118_410_542)
 * 2. Last segment of engineBaseId → 0 (e.g., 2006_73_986_123_2118_410_0)
 * 3. Last 2 segments of engineBaseId → 0 (e.g., 2006_73_986_123_2118_0_0)
 * 4. All segments of engineBaseId → 0 (e.g., 2006_73_986_123_0_0_0)
 * 5. Submodel ID → 0 and all engine segments → 0 (e.g., 2006_73_986_0_0_0_0)
 * @param vehicleMap - Vehicle summary data
 * @returns Array of ACES YMME formatted strings or empty array if data is incomplete
 */
export const formatAcesYMME = (vehicleMap?: VehicleSummary): string[] => {
  if (!vehicleMap) {
    return [];
  }

  const year = vehicleMap.year || '';
  const makeId = vehicleMap.makeId ?? 0;
  const modelId = vehicleMap.modelId ?? 0;
  const submodelId = vehicleMap.vehicleQuestionsIds?.subModelId ?? 0;
  const engineBaseId = vehicleMap?.vehicleQuestionsIds?.engineBaseId ?? '0_0_0';

  // Only return formatted strings if we have at least year, makeId, and modelId
  if (!year || !makeId || !modelId) {
    return [];
  }

  const basePrefix = `${year}_${makeId}_${modelId}_${submodelId}`;
  const engineSegments = String(engineBaseId).split('_');

  // Generate variations with progressive zeroing of engineBaseId segments
  const variations: string[] = [];

  // Full format
  variations.push(`${basePrefix}_${engineBaseId}`);

  // Replace segments from right to left
  for (let i = 1; i <= engineSegments.length; i++) {
    const modifiedSegments = [...engineSegments];
    // Replace last i segments with 0
    for (let j = engineSegments.length - i; j < engineSegments.length; j++) {
      modifiedSegments[j] = '0';
    }
    variations.push(`${basePrefix}_${modifiedSegments.join('_')}`);
  }

  // Add variation with zeroed submodel and all engine segments zeroed
  const zeroedEngineSegments = engineSegments.map(() => '0');
  variations.push(`${year}_${makeId}_${modelId}_0_${zeroedEngineSegments.join('_')}`);

  return [...new Set(variations)];
};

const VEHICLE_PARAM_KEYS = [
  'vehicleId',
  'makeId',
  'makeName',
  'modelId',
  'modelName',
  'vehicleTypeId',
  'vehicleTypeName',
  'year',
] as const;

export const buildVehicleURL = (
  vehicleMap: VehicleSummary | undefined,
  acesEnabled: boolean,
): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const hasVehicle = acesEnabled
    ? Boolean(vehicleMap?.vehicleId || vehicleMap?.vehicleName)
    : Boolean(vehicleMap?.catalogVehicleId);

  if (!hasVehicle) {
    return undefined;
  }

  const url = new URL(window.location.href);
  VEHICLE_PARAM_KEYS.forEach((key) => url.searchParams.delete(key));

  if (acesEnabled) {
    if (vehicleMap?.makeId) {
      url.searchParams.set('makeId', String(vehicleMap.makeId));
    }
    if (vehicleMap?.make) {
      url.searchParams.set('makeName', vehicleMap.make);
    }
    if (vehicleMap?.modelId) {
      url.searchParams.set('modelId', String(vehicleMap.modelId));
    }
    if (vehicleMap?.model) {
      url.searchParams.set('modelName', vehicleMap.model);
    }
    if (vehicleMap?.vehicleTypeId) {
      url.searchParams.set('vehicleTypeId', String(vehicleMap.vehicleTypeId));
    }
    if (vehicleMap?.vehicleTypeName) {
      url.searchParams.set('vehicleTypeName', vehicleMap.vehicleTypeName);
    }
    if (vehicleMap?.year) {
      url.searchParams.set('year', vehicleMap.year);
    }
  } else {
    if (vehicleMap?.catalogVehicleId) {
      url.searchParams.set('vehicleId', vehicleMap.catalogVehicleId);
    }
  }

  return url.toString();
};
